/**
 * PermissionsPage Component
 * 
 * Main page for managing permissions in Admin Console.
 * Provides tabs for Roles, Modules, and Users & Groups management.
 */

import { useState } from 'react';
import { useI18n } from '@/i18n';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { Shield, Package, Users, Key } from 'lucide-react';
import { RolesTab } from './components/RolesTab';
import { ModulesTab } from './components/ModulesTab';
import { UsersGroupsTab } from './components/UsersGroupsTab';
import { ServiceAccountsTab } from './components/ServiceAccountsTab';

export function PermissionsPage() {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState('roles');

  return (
    <div className="container mx-auto py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {t('admin.permissions.pageTitle') || 'Permissions Management'}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('admin.permissions.pageDescription') || 'Manage roles, module access, and user permissions'}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="roles" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            {t('admin.permissions.tabs.roles') || 'Roles'}
          </TabsTrigger>
          <TabsTrigger value="modules" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            {t('admin.permissions.tabs.modules') || 'Modules'}
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            {t('admin.permissions.tabs.usersGroups') || 'Users & Groups'}
          </TabsTrigger>
          <TabsTrigger value="service-accounts" className="flex items-center gap-2">
            <Key className="h-4 w-4" />
            {t('admin.permissions.tabs.serviceAccounts') || 'Service Accounts'}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="roles">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                {t('admin.permissions.roles.title') || 'Platform Roles'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RolesTab />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="modules">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                {t('admin.permissions.modules.title') || 'Module Permissions'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ModulesTab />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t('admin.permissions.usersGroups.title') || 'Users & Groups'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <UsersGroupsTab />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="service-accounts">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                {t('admin.permissions.serviceAccounts.title') || 'Service Accounts'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ServiceAccountsTab />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default PermissionsPage;
